// src/components/NineHoleScorecardTable.js
import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Typography,
} from "@mui/material";

// Helper: Computes the sum (aggregate) of numeric values.
const computeAggregate = (values) =>
  values.reduce((sum, val) => sum + (parseInt(val, 10) || 0), 0);

const NineHoleScorecardTable = ({
  scores,
  handleScoreChange,
  selectedTee,
  holeOffset,
  readOnly = false,
}) => {
  if (!selectedTee) {
    return (
      <Typography variant="body1">
        Please select a tee to view your 9‑hole scorecard.
      </Typography>
    );
  }

  const start = holeOffset;
  const end = start + 9;
  let holesToDisplay = selectedTee.holes.slice(start, end);
  // If there are fewer than 9 holes, fill missing holes with empty objects.
  while (holesToDisplay.length < 9) {
    holesToDisplay.push({
      yardage: "",
      par: "",
      handicap: "",
      hole_number: start + holesToDisplay.length + 1,
    });
  }

  const yardages = holesToDisplay.map((h) => parseInt(h.yardage, 10) || 0);
  const pars = holesToDisplay.map((h) => parseInt(h.par, 10) || 0);
  const handicaps = holesToDisplay.map((h) => h.handicap || "");
  const totalYardage = computeAggregate(yardages);
  const totalPar = computeAggregate(pars);
  const scoreSlice = scores.slice(start, end);
  const totalScore = computeAggregate(scoreSlice);

  return (
    <TableContainer component={Paper} elevation={3} sx={{ p: 2, mb: 4 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ padding: "4px" }}>Hole</TableCell>
            {holesToDisplay.map((hole, idx) => (
              <TableCell key={`hole-${idx}`} sx={{ padding: "4px" }}>
                {hole.hole_number || start + idx + 1}
              </TableCell>
            ))}
            <TableCell sx={{ padding: "4px" }}>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Yardage Row */}
          <TableRow>
            <TableCell sx={{ padding: "4px" }}>
              {selectedTee.tee_name}
            </TableCell>
            {yardages.map((y, idx) => (
              <TableCell key={`yard-${idx}`} sx={{ padding: "4px" }}>
                {y || ""}
              </TableCell>
            ))}
            <TableCell sx={{ padding: "4px" }}>
              {totalYardage || ""}
            </TableCell>
          </TableRow>
          {/* Par Row */}
          <TableRow>
            <TableCell sx={{ padding: "4px" }}>Par</TableCell>
            {pars.map((p, idx) => (
              <TableCell key={`par-${idx}`} sx={{ padding: "4px" }}>
                {p || ""}
              </TableCell>
            ))}
            <TableCell sx={{ padding: "4px" }}>{totalPar || ""}</TableCell>
          </TableRow>
          {/* Handicap Row */}
          <TableRow>
            <TableCell sx={{ padding: "4px" }}>Handicap</TableCell>
            {handicaps.map((hcp, idx) => (
              <TableCell key={`hcp-${idx}`} sx={{ padding: "4px" }}>
                {hcp || ""}
              </TableCell>
            ))}
            <TableCell sx={{ padding: "4px" }}>-</TableCell>
          </TableRow>
          {/* Score Row */}
          <TableRow>
            <TableCell sx={{ padding: "4px" }}>Score</TableCell>
            {Array.from({ length: 9 }, (_, idx) => (
              <TableCell key={`score-${idx}`} sx={{ padding: "4px" }}>
                {readOnly ? (
                  scores[start + idx] || ""
                ) : (
                  <TextField
                    type="text"
                    value={scores[start + idx] || ""}
                    onChange={(e) =>
                      handleScoreChange(start + idx, e.target.value)
                    }
                    fullWidth
                    variant="outlined"
                    size="small"
                    inputProps={{
                      style: { fontSize: "0.8rem", padding: "4px" },
                      autoComplete: "off",
                    }}
                  />
                )}
              </TableCell>
            ))}
            <TableCell sx={{ padding: "4px" }}>
              {totalScore || ""}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default NineHoleScorecardTable;